'use client';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../utils';

const ToastProvider = ToastPrimitives.Provider;

// Where toasts sit. By default: the top on phones, bottom-right from 640px. With
// `underAppBar` (the dashboard, docs/design-system.md, Saving a field): top-right below
// the app bar on desktop, the bottom above the safe area on phones, and `flipped` moves
// a toast to the other place when it would cover the field it is about.
const ToastViewport = ({
  className,
  underAppBar = false,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> & { underAppBar?: boolean }) => (
  <ToastPrimitives.Viewport
    className={cn(
      underAppBar
        ? [
            'fixed bottom-0 left-0 z-[100] flex max-h-screen w-full flex-col p-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
            'data-[placement=flipped]:bottom-auto data-[placement=flipped]:top-[var(--app-bar-height,3.5rem)] data-[placement=flipped]:pb-4',
            'sm:bottom-auto sm:left-auto sm:right-0 sm:top-[var(--app-bar-height,3.5rem)] sm:max-w-[420px] sm:pb-4',
            'sm:data-[placement=flipped]:bottom-0 sm:data-[placement=flipped]:top-auto',
          ]
        : 'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      className,
    )}
    {...props}
  />
);
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-8 shadow-lg touch:pr-12 transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        destructive:
          'destructive group border-destructive bg-destructive text-destructive-foreground',
        success: 'success group border-emerald-500 bg-emerald-100 text-success-foreground',
      },
      // Slides in from the edge its viewport sits on (ToastViewport).
      position: {
        default:
          'data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
        underAppBar:
          'data-[state=open]:slide-in-from-bottom-full data-[state=open]:sm:slide-in-from-top-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      position: 'default',
    },
  },
);

const Toast = ({
  className,
  variant,
  position,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants>) => {
  return (
    <ToastPrimitives.Root
      className={cn(toastVariants({ variant, position }), className)}
      {...props}
    />
  );
};
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>) => (
  <ToastPrimitives.Action
    className={cn(
      'hover:bg-secondary focus:ring-ring group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive touch:h-10 inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-1 disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
);
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>) => (
  <ToastPrimitives.Close
    className={cn(
      // Always visible: no hover-only controls (a toast that stays needs a way out on touch).
      'text-foreground/60 hover:text-foreground focus:ring-ring touch:size-10 group-[.destructive]:text-destructive-foreground/80 group-[.destructive]:hover:text-destructive-foreground absolute right-1 top-1 flex size-6 items-center justify-center rounded-md focus:outline-none focus:ring-1',
      className,
    )}
    toast-close=""
    {...props}
  >
    <Cross2Icon className="h-4 w-4" />
  </ToastPrimitives.Close>
);
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>) => (
  <ToastPrimitives.Title
    className={cn('text-sm font-semibold [&+div]:text-xs', className)}
    {...props}
  />
);
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>) => (
  <ToastPrimitives.Description className={cn('text-sm opacity-90', className)} {...props} />
);
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
